import Phaser from "phaser";
import type { LaneNumber } from "../../../../shared/types/CommanderDecision";

export type UnitFaction = "player" | "enemy";

export type UnitWaypoint = {
  x: number;
  y: number;
  lane?: LaneNumber;
};

export type UnitCommandMode = "move" | "advance" | "hold" | "retreat" | "rotate" | "reinforce";
export type UnitManualAbilityId = "none" | "shield_wall" | "fire_burst" | "repair_burst" | "rally_banner";
export type UnitManualAbilityState = {
  id: Exclude<UnitManualAbilityId, "none">;
  cooldownMs: number;
  activeDurationMs: number;
  cooldownRemainingMs: number;
  activeRemainingMs: number;
  ready: boolean;
};

export type UnitConfig = {
  id: string;
  name: string;
  role?: string;
  lane: LaneNumber;
  x: number;
  y: number;
  faction: UnitFaction;
  texture: string;
  hp: number;
  damage: number;
  range: number;
  attackRateMs: number;
  speed: number;
  castleDamage?: number;
  goldReward?: number;
  splashRadius?: number;
  slow?: number;
  freezeChance?: number;
  convertChance?: number;
  scoutCoverage?: boolean;
  ability?: string;
  targetPriority?: "units" | "towers" | "castle" | "siege";
  siegeDamageMultiplier?: number;
  dot?: { damage: number; durationMs: number };
  summon?: string;
  explodeOnDeath?: { damage: number; radius: number };
  buffAura?: number;
  repairPower?: number;
  deployTimeMs?: number;
  manualAbility?: UnitManualAbilityId;
};

export class Unit {
  readonly id: string;
  readonly name: string;
  readonly role: string;
  lane: LaneNumber;
  readonly faction: UnitFaction;
  damage: number;
  range: number;
  attackRateMs: number;
  baseSpeed: number;
  readonly castleDamage: number;
  readonly goldReward: number;
  readonly splashRadius: number;
  readonly slow: number;
  readonly freezeChance: number;
  readonly convertChance: number;
  readonly scoutCoverage: boolean;
  readonly ability: string;
  readonly targetPriority: "units" | "towers" | "castle" | "siege";
  readonly siegeDamageMultiplier: number;
  readonly dot?: { damage: number; durationMs: number };
  readonly summon?: string;
  readonly explodeOnDeath?: { damage: number; radius: number };
  readonly buffAura: number;
  readonly repairPower: number;
  readonly deployTimeMs: number;
  readonly manualAbility: UnitManualAbilityId;
  readonly sprite: Phaser.GameObjects.Sprite;
  private readonly shadow: Phaser.GameObjects.Ellipse;
  private readonly hpBack: Phaser.GameObjects.Rectangle;
  private readonly hpFill: Phaser.GameObjects.Rectangle;
  private readonly deployRing?: Phaser.GameObjects.Graphics;
  private veteranBadge?: Phaser.GameObjects.Container;
  private veteranBonusApplied = false;
  private walkTween?: Phaser.Tweens.Tween;
  private hpVisibleUntil = 0;
  private movementBlocked = false;
  private waypoints: UnitWaypoint[] = [];
  private holdUntil = 0;
  // When true the unit stays put instead of auto-marching forward. Set by player
  // "move"/"hold"/"retreat" commands; "advance" clears it so the unit keeps
  // pushing toward the enemy. Enemies never set it, so they always march.
  private holdPosition = false;
  private facingDirection: -1 | 1 = -1;
  private commandMode: UnitCommandMode = "move";
  private objectiveVulnerableUntil = 0;
  private objectiveVulnerabilityMultiplier = 1;
  private deploymentStartedAt = 0;
  private deploymentReadyAt = 0;
  private nextManualAbilityAt = 0;
  private manualAbilityActiveUntil = 0;

  hp: number;
  maxHp: number;
  level = 1;
  nextAttackAt = 0;
  slowUntil = 0;
  slowMultiplier = 1;
  terrainSpeedMultiplier = 1;
  alive = true;
  lastSummonAt = 0;
  isVeteran = false;
  veteranNickname?: string;

  constructor(private readonly scene: Phaser.Scene, config: UnitConfig) {
    this.id = config.id;
    this.name = config.name;
    this.role = config.role ?? "unknown";
    this.lane = config.lane;
    this.faction = config.faction;
    this.damage = config.damage;
    this.range = config.range;
    this.attackRateMs = config.attackRateMs;
    this.baseSpeed = config.speed;
    this.castleDamage = config.castleDamage ?? 0;
    this.goldReward = config.goldReward ?? 0;
    this.splashRadius = config.splashRadius ?? 0;
    this.slow = config.slow ?? 0;
    this.freezeChance = config.freezeChance ?? 0;
    this.convertChance = config.convertChance ?? 0;
    this.scoutCoverage = config.scoutCoverage ?? false;
    this.ability = config.ability ?? "none";
    this.targetPriority = config.targetPriority ?? "units";
    this.siegeDamageMultiplier = config.siegeDamageMultiplier ?? 1;
    this.dot = config.dot;
    this.summon = config.summon;
    this.explodeOnDeath = config.explodeOnDeath;
    this.buffAura = config.buffAura ?? 0;
    this.repairPower = config.repairPower ?? 0;
    this.deployTimeMs = config.deployTimeMs ?? 0;
    this.manualAbility = config.manualAbility ?? this.defaultManualAbility(config.id, config.role);
    this.hp = config.hp;
    this.maxHp = config.hp;
    if (config.faction === "player") {
      this.applyInitialGrowth();
    }

    this.shadow = scene.add.ellipse(config.x, config.y + 18, 34, 11, 0x030305, 0.42).setDepth(config.faction === "player" ? 43 : 40);
    this.sprite = scene.add.sprite(config.x, config.y, config.texture);
    this.sprite.setDepth(config.faction === "player" ? 45 : 42);
    this.applyDisplaySize(config.texture);
    this.faceHorizontal(config.faction === "enemy" ? 1 : -1);
    this.startWalkAnimation(config.texture);

    this.hpBack = scene.add.rectangle(config.x, config.y - 27, 34, 5, 0x08070a, 0.9).setDepth(80).setVisible(false);
    this.hpFill = scene.add
      .rectangle(config.x - 16, config.y - 27, 32, 3, config.faction === "player" ? 0x70d7ff : 0xff604f, 0.95)
      .setOrigin(0, 0.5)
      .setDepth(81)
      .setVisible(false);
    if (this.deployTimeMs > 0) {
      this.deployRing = scene.add.graphics().setDepth(82);
      this.startDeployment(scene.time.now, this.deployTimeMs);
    }
  }

  get x() {
    return this.sprite.x;
  }

  get y() {
    return this.sprite.y;
  }

  update(time: number, deltaMs: number) {
    if (!this.alive) return;

    if (time > this.slowUntil) {
      this.slowMultiplier = 1;
    }
    this.updateDeployRing(time);

    if (!this.movementBlocked) {
      const distance = (this.baseSpeed * this.slowMultiplier * this.terrainSpeedMultiplier * deltaMs) / 1000;
      if (this.waypoints.length > 0) {
        this.advanceAlongWaypoints(distance, time);
      } else if (!this.holdPosition && time >= this.holdUntil) {
        const direction = this.faction === "enemy" ? 1 : -1;
        this.faceHorizontal(direction);
        this.sprite.x += direction * distance;
      }
    }
    if (this.walkTween) {
      this.walkTween.timeScale = this.movementBlocked ? 0.45 : 1;
    }
    this.syncBars();
    const showHp = this.hp < this.maxHp && time < this.hpVisibleUntil;
    this.hpBack.setVisible(showHp);
    this.hpFill.setVisible(showHp);
  }

  canAttack(time: number) {
    return this.alive && time >= this.nextAttackAt && time >= this.deploymentReadyAt;
  }

  markAttack(time: number) {
    this.nextAttackAt = time + this.attackRateMs;
    if (this.holdUntil > time) {
      this.holdUntil = time + 6000;
    }
    this.playAttackFeedback();
  }

  setMovementBlocked(blocked: boolean) {
    this.movementBlocked = blocked;
    const animationState = this.sprite.anims;
    if (animationState?.isPlaying) {
      animationState.timeScale = blocked ? 0.55 : 1;
    }
    if (this.walkTween) {
      this.walkTween.timeScale = blocked ? 0.45 : 1;
    }
  }

  setTerrainSpeedMultiplier(multiplier: number) {
    this.terrainSpeedMultiplier = Math.max(0.35, multiplier);
  }

  setX(x: number) {
    this.sprite.x = x;
    this.syncBars();
  }

  setY(y: number) {
    this.sprite.y = y;
    this.syncBars();
  }

  commandMove(waypoints: UnitWaypoint[], time: number, holdMs = 6000, mode: UnitCommandMode = "move") {
    this.waypoints = waypoints.slice();
    this.commandMode = mode;
    // "advance" keeps marching forward after arriving; every other player order
    // means "go here and stay" so the unit no longer drifts forward on its own.
    this.holdPosition = mode !== "advance";
    this.holdUntil = this.waypoints.length > 0 ? 0 : time + holdMs;
    if (this.deployTimeMs > 0) {
      this.startDeployment(time, Math.min(this.deployTimeMs, 1800));
    }
    this.sprite.setTint(this.faction === "player" ? 0x8df3ff : 0xffc56b);
    this.scene.time.delayedCall(160, () => {
      if (this.alive) this.sprite.clearTint();
    });
  }

  /** Stop the unit where it stands and keep it there until a new order. */
  holdInPlace(time: number) {
    this.waypoints = [];
    this.commandMode = "hold";
    this.holdPosition = true;
    this.holdUntil = time;
    this.sprite.setTint(this.faction === "player" ? 0x8df3ff : 0xffc56b);
    this.scene.time.delayedCall(160, () => {
      if (this.alive) this.sprite.clearTint();
    });
  }

  /** Resume marching toward the enemy from the current position. */
  resumeAdvance() {
    this.waypoints = [];
    this.commandMode = "advance";
    this.holdPosition = false;
    this.holdUntil = 0;
  }

  isHolding() {
    return this.holdPosition && this.waypoints.length === 0;
  }

  clearMoveCommand(time: number, holdMs = 6000) {
    this.waypoints = [];
    this.commandMode = "move";
    this.holdPosition = false;
    this.holdUntil = time + holdMs;
  }

  isRetreating() {
    return this.commandMode === "retreat" && this.waypoints.length > 0;
  }

  isDeploying(time = this.scene.time.now) {
    return this.alive && time < this.deploymentReadyAt;
  }

  applySlow(amount: number, until: number) {
    this.slowMultiplier = Math.max(0.25, 1 - amount);
    this.slowUntil = Math.max(this.slowUntil, until);
  }

  applyObjectiveVulnerability(until: number, multiplier: number) {
    this.objectiveVulnerableUntil = Math.max(this.objectiveVulnerableUntil, until);
    this.objectiveVulnerabilityMultiplier = Math.max(this.objectiveVulnerabilityMultiplier, multiplier);
    this.sprite.setTint(0xffd36e);
    this.scene.time.delayedCall(140, () => {
      if (this.alive && this.scene.time.now >= this.objectiveVulnerableUntil) this.sprite.clearTint();
    });
  }

  getManualAbilityState(time = this.scene.time.now): UnitManualAbilityState | undefined {
    if (this.manualAbility === "none") return undefined;
    const config = this.manualAbilityConfig(this.manualAbility);
    return {
      id: this.manualAbility,
      cooldownMs: config.cooldownMs,
      activeDurationMs: config.activeDurationMs,
      cooldownRemainingMs: Math.max(0, this.nextManualAbilityAt - time),
      activeRemainingMs: Math.max(0, this.manualAbilityActiveUntil - time),
      ready: this.alive && time >= this.nextManualAbilityAt
    };
  }

  activateManualAbility(time = this.scene.time.now) {
    if (this.manualAbility === "none" || !this.alive || time < this.nextManualAbilityAt) return false;
    const config = this.manualAbilityConfig(this.manualAbility);
    this.nextManualAbilityAt = time + config.cooldownMs;
    if (config.activeDurationMs > 0) {
      this.manualAbilityActiveUntil = Math.max(this.manualAbilityActiveUntil, time + config.activeDurationMs);
    }
    this.sprite.setTint(this.manualAbility === "shield_wall" ? 0x9feaff : this.manualAbility === "rally_banner" ? 0xffe58e : 0xa7ffcf);
    this.scene.time.delayedCall(220, () => {
      if (this.alive && !this.hasActiveManualAbility(this.manualAbility, this.scene.time.now)) this.sprite.clearTint();
    });
    return true;
  }

  hasActiveManualAbility(ability: UnitManualAbilityId, time = this.scene.time.now) {
    return this.manualAbility === ability && time < this.manualAbilityActiveUntil;
  }

  effectiveBuffAura(time = this.scene.time.now) {
    return this.buffAura + (this.hasActiveManualAbility("rally_banner", time) ? 0.18 : 0);
  }

  objectiveDamageTakenMultiplier(time: number) {
    if (time > this.objectiveVulnerableUntil) {
      this.objectiveVulnerabilityMultiplier = 1;
      return 1;
    }
    return this.objectiveVulnerabilityMultiplier;
  }

  takeDamage(amount: number) {
    if (!this.alive || amount <= 0) return;
    const finalAmount = amount * this.manualDamageTakenMultiplier(this.scene.time.now);
    this.hp = Math.max(0, this.hp - finalAmount);
    this.hpVisibleUntil = this.scene.time.now + 1800;
    this.sprite.setTintFill(0xffffff);
    this.scene.time.delayedCall(70, () => {
      if (this.alive) this.sprite.clearTint();
    });
    this.syncBars();
    if (this.hp <= 0) {
      this.alive = false;
      this.sprite.setVisible(false);
      this.hpBack.setVisible(false);
      this.hpFill.setVisible(false);
      this.destroy();
    }
  }

  heal(amount: number) {
    if (!this.alive || amount <= 0) return;
    this.hp = Math.min(this.maxHp, this.hp + amount);
    this.hpVisibleUntil = this.scene.time.now + 1000;
    this.syncBars();
  }

  upgrade() {
    if (!this.alive) return [];
    this.level += 1;
    const statCount = Phaser.Math.Between(1, 3);
    const stats = Phaser.Utils.Array.Shuffle(["hp", "damage", "range", "speed", "rate"]);
    const changed: string[] = [];
    for (const stat of stats.slice(0, statCount)) {
      if (stat === "hp") {
        const gain = Phaser.Math.Between(12, 42);
        this.maxHp += gain;
        this.hp += gain;
      } else if (stat === "damage") {
        this.damage = Math.round(this.damage + Phaser.Math.Between(2, 9));
      } else if (stat === "range") {
        this.range = Math.round(this.range + Phaser.Math.Between(5, 22));
      } else if (stat === "speed") {
        this.baseSpeed = Math.round(this.baseSpeed + Phaser.Math.Between(3, 12));
      } else {
        this.attackRateMs = Math.max(260, Math.round(this.attackRateMs * Phaser.Math.FloatBetween(0.9, 0.97)));
      }
      changed.push(stat);
    }
    this.hpVisibleUntil = this.scene.time.now + 1800;
    this.sprite.setTint(0x8df3ff);
    this.scene.time.delayedCall(180, () => {
      if (this.alive) this.sprite.clearTint();
    });
    this.syncBars();
    return changed;
  }

  applyVeteran(nickname: string, bonus = { hpRatio: 0.12, damageRatio: 0.08, speedRatio: 0.04 }) {
    if (!this.alive) return;
    this.isVeteran = true;
    this.veteranNickname = nickname;
    if (!this.veteranBonusApplied) {
      const hpGain = Math.max(1, Math.round(this.maxHp * bonus.hpRatio));
      this.maxHp += hpGain;
      this.hp += hpGain;
      if (this.damage > 0) {
        this.damage = Math.max(1, Math.round(this.damage * (1 + bonus.damageRatio)));
      }
      this.baseSpeed = Math.round(this.baseSpeed * (1 + bonus.speedRatio));
      this.veteranBonusApplied = true;
    }
    this.createVeteranBadge();
    this.sprite.setTint(0xfff0a6);
    this.scene.time.delayedCall(220, () => {
      if (this.alive) this.sprite.clearTint();
    });
    this.syncBars();
  }

  private applyInitialGrowth() {
    const stat = Phaser.Utils.Array.GetRandom(["hp", "damage", "range", "speed"]);
    if (stat === "hp") {
      const gain = Phaser.Math.Between(4, 16);
      this.hp += gain;
      this.maxHp += gain;
    } else if (stat === "damage") {
      this.damage = Math.round(this.damage + Phaser.Math.Between(1, 4));
    } else if (stat === "range") {
      this.range = Math.round(this.range + Phaser.Math.Between(3, 12));
    } else {
      this.baseSpeed = Math.round(this.baseSpeed + Phaser.Math.Between(2, 7));
    }
  }

  private defaultManualAbility(id: string, role?: string): UnitManualAbilityId {
    if (id === "shield_soldier" || role === "defense") return "shield_wall";
    if (id === "powder_carrier" || id === "alchemist") return "fire_burst";
    if (id === "engineer" || role === "repair") return "repair_burst";
    if (id === "standard_bearer" || role === "buffer") return "rally_banner";
    return "none";
  }

  private manualAbilityConfig(ability: Exclude<UnitManualAbilityId, "none">) {
    if (ability === "shield_wall") return { cooldownMs: 26000, activeDurationMs: 8000 };
    if (ability === "fire_burst") return { cooldownMs: 24000, activeDurationMs: 0 };
    if (ability === "repair_burst") return { cooldownMs: 28000, activeDurationMs: 0 };
    return { cooldownMs: 32000, activeDurationMs: 10000 };
  }

  private manualDamageTakenMultiplier(time: number) {
    return this.hasActiveManualAbility("shield_wall", time) ? 0.55 : 1;
  }

  destroy() {
    this.veteranBadge?.destroy();
    this.deployRing?.destroy();
    this.sprite.destroy();
    this.shadow.destroy();
    this.hpBack.destroy();
    this.hpFill.destroy();
    this.walkTween?.stop();
  }

  private startWalkAnimation(texture: string) {
    if (!this.sprite.anims) return;

    const frameCount = this.scene.textures.get(texture).frameTotal;
    if (frameCount <= 1) {
      this.startSingleFrameWalkTween();
      return;
    }

    const key = `walk_${texture}`;
    if (!this.scene.anims.exists(key)) {
      this.scene.anims.create({
        key,
        frames: this.scene.anims.generateFrameNumbers(texture, { start: 0, end: Math.min(1, frameCount - 1) }),
        frameRate: this.id === "scout_wolf" || this.id === "raven_scout" ? 7 : 5,
        repeat: -1
      });
    }
    this.sprite.play(key);
  }

  private applyDisplaySize(texture: string) {
    const textureObject = this.scene.textures.get(texture);
    const frame = textureObject.get(0);
    const frameWidth = frame?.width || 96;
    const frameHeight = frame?.height || 96;
    if (textureObject.frameTotal > 1 || (frameWidth <= 128 && frameHeight <= 128)) {
      this.sprite.setScale(this.id === "siege_cart" ? 0.58 : this.id === "scout_wolf" ? 0.52 : 0.56);
      return;
    }

    const maxHeight = this.faction === "enemy" && (this.id === "heavy_ram" || this.id === "armored_beast") ? 66 : 72;
    const maxWidth = this.faction === "enemy" && (this.id === "heavy_ram" || this.id === "armored_beast") ? 90 : 62;
    const ratio = Math.min(maxWidth / frameWidth, maxHeight / frameHeight);
    this.sprite.setDisplaySize(Math.round(frameWidth * ratio), Math.round(frameHeight * ratio));
  }

  private startSingleFrameWalkTween() {
    const baseScaleY = this.sprite.scaleY;
    this.walkTween = this.scene.tweens.add({
      targets: this.sprite,
      scaleY: baseScaleY * 1.04,
      yoyo: true,
      repeat: -1,
      duration: this.id === "scout_wolf" ? 240 : 360,
      ease: "Sine.easeInOut"
    });
  }

  private startDeployment(time: number, durationMs: number) {
    this.deploymentStartedAt = time;
    this.deploymentReadyAt = Math.max(this.deploymentReadyAt, time + durationMs);
    this.nextAttackAt = Math.max(this.nextAttackAt, this.deploymentReadyAt);
    this.deployRing?.setVisible(true);
  }

  private updateDeployRing(time: number) {
    if (!this.deployRing) return;
    if (time >= this.deploymentReadyAt) {
      this.deployRing.clear();
      this.deployRing.setVisible(false);
      return;
    }
    const duration = Math.max(1, this.deploymentReadyAt - this.deploymentStartedAt);
    const progress = Phaser.Math.Clamp((time - this.deploymentStartedAt) / duration, 0, 1);
    const x = this.sprite.x;
    const y = this.sprite.y - 34;
    this.deployRing.clear();
    this.deployRing.lineStyle(3, 0xffd36e, 0.72);
    this.deployRing.beginPath();
    this.deployRing.arc(x, y, 12, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * progress, false);
    this.deployRing.strokePath();
    this.deployRing.lineStyle(1, 0x140b03, 0.8);
    this.deployRing.strokeCircle(x, y, 13);
  }

  private playAttackFeedback() {
    const lean = this.facingDirection * 5;
    this.scene.tweens.add({
      targets: this.sprite,
      x: this.sprite.x + lean,
      yoyo: true,
      duration: 70,
      ease: "Sine.easeOut"
    });
  }

  private advanceAlongWaypoints(distance: number, time: number) {
    const waypoint = this.waypoints[0];
    if (!waypoint) return;

    const dx = waypoint.x - this.sprite.x;
    const dy = waypoint.y - this.sprite.y;
    const length = Math.hypot(dx, dy);
    if (length <= Math.max(2, distance)) {
      this.sprite.x = waypoint.x;
      this.sprite.y = waypoint.y;
      if (waypoint.lane) {
        this.lane = waypoint.lane;
      }
      this.waypoints.shift();
      if (this.waypoints.length === 0) {
        // On arrival: "advance" keeps marching (holdPosition false → resumes next
        // frame); every other order holds, enforced by holdPosition in update().
        this.holdUntil = time;
      }
      return;
    }

    if (Math.abs(dx) > 1.5) {
      this.faceHorizontal(dx > 0 ? 1 : -1);
    }
    this.sprite.x += (dx / length) * distance;
    this.sprite.y += (dy / length) * distance;
  }

  private faceHorizontal(direction: -1 | 1) {
    this.facingDirection = direction;
    this.sprite.setFlipX(direction > 0);
  }

  private syncBars() {
    this.shadow.setPosition(this.sprite.x, this.sprite.y + 18);
    this.hpBack.setPosition(this.sprite.x, this.sprite.y - 27);
    this.hpFill.setPosition(this.sprite.x - 16, this.sprite.y - 27);
    this.hpFill.width = 32 * Math.max(0, this.hp / this.maxHp);
    this.veteranBadge?.setPosition(this.sprite.x + 18, this.sprite.y - 31);
  }

  private createVeteranBadge() {
    if (this.veteranBadge) return;
    const backing = this.scene.add.ellipse(0, 0, 15, 15, 0x21150a, 0.94).setStrokeStyle(1, 0xffd36e, 0.95);
    const label = this.scene.add
      .text(0, -1, "V", {
        fontFamily: "\"Apple SD Gothic Neo\", \"Noto Sans KR\", \"Malgun Gothic\", sans-serif",
        fontSize: "10px",
        color: "#ffe58e",
        stroke: "#111016",
        strokeThickness: 2
      })
      .setResolution(2)
      .setOrigin(0.5);
    this.veteranBadge = this.scene.add.container(this.sprite.x + 18, this.sprite.y - 31, [backing, label]).setDepth(84);
  }
}
